import { useState, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Box, Button, Grid, Typography, useTheme } from '@mui/material'
import { ActivityIndicator } from 'components/Shared/ActivityIndicator'
import { Page } from 'components/Shared/Page'
import { PageHeader } from 'components/Shared/PageHeader'
import { SectionHeader } from 'components/SectionHeader'
import { SimpleImageCard } from 'components/ImageCard/SimpleImageCard'
import { formatDateTime } from 'utils/util'
import { LocationBadgesEdit } from './LocationBadgesEdit'
import { useGetBadgeRewardById } from 'hooks/api/Contests/BadgeReward/useGetBadgeRewardsById'
import { useGetSweepstakesByTypeWithGrouping } from 'hooks/api/useGetSweepstakesByTypeWithGrouping'
import { BadgesUploadImage } from '../BadgesUploadImage'
import { useGetPatronBadgeCountsByBadgeId } from 'hooks/api/useGetPatronBadgeCountsByBadgeId'
import { type BadgeRewardType } from 'types/api'
import { useGetCorporateAccountById } from 'hooks/api/useGetCorporateAccountById'
import { useGetOrganizationById } from 'hooks/api/useGetOrganizationById'
import { useGetRewardProgramById } from 'hooks/api/useGetRewardProgramById'
import { ViewLocationsModal } from 'pages/Communications/Messages/MessageDetailPage/ViewLocationsModal'
import {
  badgeRewardTypeHasAccessToControl,
  getBadgeRewardTypeName,
} from 'utils/badgeRewardsUtils'
import { useGetAllLocationBadges } from 'hooks/api/Contests/BadgeReward/useGetAllLocationBadges'

interface BadgeRewardDetailView {
  id: number
  name: string
  type: BadgeRewardType
  description?: string | null
  startDate?: string | null
  endDate?: string | null
  renewableType?: string | null
  dependentBadgeRewardId?: number | null
  locationPointsAwarded?: number | null
  locationSweepstakeEntriesAwarded?: number | null
  locationSweepstakeContestId?: number | null
  locationSweepstakeGroupId?: string | null
  count?: number | null
  badgeRewardSchedules?: Array<{ dayName?: string | null }>
  imageNotEarned?: string | null
  imageEarned?: string | null
}

interface SweepstakeView {
  id: number
  name: string
  groupId?: string | null
}

const hasBadgeCounts = (
  value: unknown
): value is {
  totalCount?: number
  uniqueCount?: number
  totalLocationPointsCount?: number
  totalLocationSweepstakeEntriesCount?: number
} => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'totalCount' in value &&
    'uniqueCount' in value &&
    'totalLocationPointsCount' in value &&
    'totalLocationSweepstakeEntriesCount' in value
  )
}

const isBadgeRewardSummary = (
  value: unknown
): value is { id: number; name: string } => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    typeof (value as { id?: unknown }).id === 'number' &&
    'name' in value &&
    typeof (value as { name?: unknown }).name === 'string'
  )
}

const isBadgeReward = (value: unknown): value is BadgeRewardDetailView => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'name' in value &&
    'type' in value
  )
}

const isSweepstake = (value: unknown): value is SweepstakeView => {
  return (
    typeof value === 'object' &&
    value !== null &&
    'id' in value &&
    'name' in value
  )
}
const getBadgeSubtypeName = (subtype?: string) => {
  switch (subtype) {
    case 'CorporateAccount':
      return 'Corporate Account'
    case 'Organization':
      return 'Organization'
    case 'Location':
      return 'Location'
    default:
      return '-'
  }
}

export const LocationBadgesDetailPage = () => {
  const params = useParams()
  const theme = useTheme()
  const id = Number(useParams().id)
  const [isImageModalOpen, setIsImageModalOpen] = useState(false)
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [isViewLocationsModalOpen, setIsViewLocationsModalOpen] =
    useState(false)
  const toggleViewLocationsModalIsOpen = () =>
    setIsViewLocationsModalOpen(!isViewLocationsModalOpen)
  const [imageType, setImageType] = useState<'notEarned' | 'earned'>(
    'notEarned'
  )

  const getPatronBadgeCountQuery = useGetPatronBadgeCountsByBadgeId({
    id,
  })
  const badgeCountData: unknown = getPatronBadgeCountQuery.data
  const totalCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalCount
    : undefined
  const uniqueCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.uniqueCount
    : undefined
  const totalLocationPointsCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalLocationPointsCount
    : undefined
  const totalLocationSweepstakeEntriesCount = hasBadgeCounts(badgeCountData)
    ? badgeCountData.totalLocationSweepstakeEntriesCount
    : undefined

  const allLocationBadgesQuery = useGetAllLocationBadges()
  const locationBadges = useMemo(() => {
    // remove the badge reward that is currently being edited from allLocationBadges to avoid having the option to select itself as a dependent badge reward
    const locationBadgesData: unknown = allLocationBadgesQuery.data
    const list = Array.isArray(locationBadgesData)
      ? locationBadgesData.filter(isBadgeRewardSummary)
      : []
    const filteredLocationBadges = list.filter(
      (badgeRewardItem) => badgeRewardItem.id !== Number(params.id)
    )

    return filteredLocationBadges.reduce<Record<number, string>>((acc, br) => {
      acc[br.id] = br.name
      return acc
    }, {})
  }, [allLocationBadgesQuery.data, params.id])

  const toggleImageModalIsOpen = async (
    type: 'notEarned' | 'earned' = 'notEarned'
  ) => {
    setImageType(type)
    setIsImageModalOpen(!isImageModalOpen)
  }

  const toggleDetailsModalIsOpen = async () => {
    setIsDetailsModalOpen(!isDetailsModalOpen)
  }

  const badgeRewardQuery = useGetBadgeRewardById({
    badgeRewardId: Number(params.id),
  })
  const locationSweepstakesQuery = useGetSweepstakesByTypeWithGrouping({
    type: 'LocationSweepstake',
  })

  const corporateAccountId = badgeRewardQuery.data?.corporateAccountId ?? 0
  const organizationId = badgeRewardQuery.data?.organizationId ?? 0

  const corporateAccountQuery = useGetCorporateAccountById({
    corporateAccountId,
    enabled:
      badgeRewardQuery.data?.subtype === 'CorporateAccount' &&
      corporateAccountId > 0,
  })
  const organizationQuery = useGetOrganizationById({ organizationId })

  const rewardsProgramId = badgeRewardQuery.data?.rewardsProgramId ?? 0
  const rewardProgramQuery = useGetRewardProgramById(rewardsProgramId)

  if (badgeRewardQuery.isPending) {
    return <ActivityIndicator />
  }
  if (badgeRewardQuery.isError) {
    return <Typography>There was an error</Typography>
  }

  const badgeReward = isBadgeReward(badgeRewardQuery.data)
    ? badgeRewardQuery.data
    : null

  if (!badgeReward?.id) {
    return <Typography>Badge reward not found</Typography>
  }

  const badgeRewardType = badgeReward.type as any

  const locationSweepstakeName = (() => {
    const allLocationSweepstakesData: unknown = locationSweepstakesQuery.data
    const locationSweepstakes = Array.isArray(allLocationSweepstakesData)
      ? allLocationSweepstakesData.filter(isSweepstake)
      : []

    if (badgeReward.locationSweepstakeContestId) {
      return (
        locationSweepstakes.find(
          (sweepstake) =>
            sweepstake.id === badgeReward.locationSweepstakeContestId
        )?.name ?? ''
      )
    }

    if (badgeReward.locationSweepstakeGroupId) {
      return (
        locationSweepstakes.find(
          (sweepstake) =>
            sweepstake.groupId === badgeReward.locationSweepstakeGroupId
        )?.name ?? ''
      )
    }

    return ''
  })()

  return (
    <Page
      header={
        <PageHeader
          title={badgeReward?.name ?? ''}
          backText="Location Badges"
          backPath="/Contests/LocationBadges"
          isSecondary={true}
        />
      }
    >
      <Box className="gap-12 flex flex-col">
        <Box>
          <SectionHeader
            title="Badge Details"
            onClickButton={toggleDetailsModalIsOpen}
          />
          <Box
            className="flex-1"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '40px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              p: '24px',
            }}
          >
            <Grid className="flex-1">
              <Grid className="flex-1 pb-4">
                <div className="grid md:grid-cols-4 gap-y-6">
                  <Grid item md={3}>
                    <Typography variant="body2" mb={1} color="text.secondary">
                      Type
                    </Typography>
                    <Typography variant="body-1" mb={2}>
                      {getBadgeRewardTypeName(badgeRewardType)}
                    </Typography>
                  </Grid>
                  <Grid item md={3}>
                    <Typography variant="body2" mb={1} color="text.secondary">
                      SubType
                    </Typography>
                    <Typography variant="body-1" mb={2}>
                      {getBadgeSubtypeName(badgeReward?.subtype)}
                    </Typography>
                  </Grid>
                  {badgeReward?.subtype === 'CorporateAccount' && (
                    <Grid item md={3}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        Corporate Account
                      </Typography>
                      <Typography variant="body-1" mb={2}>
                        {corporateAccountQuery.data?.name ?? '-'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeReward?.subtype === 'Organization' && (
                    <Grid item md={3}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        Organization
                      </Typography>
                      <Typography variant="body-1" mb={2}>
                        {organizationQuery.data?.name ?? '-'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeReward?.subtype === 'Location' && (
                    <Grid item md={3}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        Licensed Establishments
                      </Typography>
                      <Box display="flex" alignItems="flex-end">
                        <Button
                          variant="contained"
                          onClick={() => setIsViewLocationsModalOpen(true)}
                        >
                          View Locations
                        </Button>
                        <Typography style={{ marginLeft: '10px' }}>
                          {`${badgeReward?.licensedEstablishmentIds?.length ?? 0} location(s) selected`}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  <div>
                    <Typography variant="body-3">Description</Typography>
                    <Typography variant="body-1">
                      {badgeReward?.description ? badgeReward.description : '-'}
                    </Typography>
                  </div>

                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'StartDateTime'
                  ) && (
                    <Grid item md={3}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        Start Date
                      </Typography>
                      <Typography variant="body1" mb={2}>
                        {badgeReward?.startDate
                          ? formatDateTime(badgeReward?.startDate, 'P p')
                          : 'None'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'EndDateTime'
                  ) && (
                    <Grid item md={3}>
                      <Typography variant="body2" mb={1} color="text.secondary">
                        End Date
                      </Typography>
                      <Typography variant="body1" mb={2}>
                        {badgeReward?.endDate
                          ? formatDateTime(badgeReward?.endDate, 'P p')
                          : 'None'}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'Schedule'
                  ) && (
                    <Grid item sm={8} md={3} lg={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          mb={1}
                          color="text.secondary"
                        >
                          Scheduled Days
                        </Typography>
                        <Typography variant="body1">
                          {badgeReward?.badgeRewardSchedules &&
                          badgeReward?.badgeRewardSchedules?.length > 0
                            ? 'Yes'
                            : 'No'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'Count'
                  ) && (
                    <Grid item sm={8} md={3} lg={3}>
                      <Box>
                        <Typography
                          variant="body2"
                          mb={1}
                          color="text.secondary"
                        >
                          {getBadgeRewardTypeName(badgeRewardType)} Count
                        </Typography>
                        <Typography variant="body1">
                          {badgeReward?.count === 0 ? '' : badgeReward?.count}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </div>
              </Grid>

              <Grid className="flex-1">
                <Grid className="flex-1 pb-4">
                  <div className="grid md:grid-cols-4 gap-y-6">
                    {badgeRewardTypeHasAccessToControl(
                      badgeRewardType,
                      'Renewable'
                    ) && (
                      <Grid item md={3}>
                        <Typography
                          variant="body2"
                          pb={1}
                          color="text.secondary"
                        >
                          Renewable
                        </Typography>
                        <Typography variant="body-1">
                          {(() => {
                            switch (badgeReward?.renewableType ?? '') {
                              case 'Daily':
                                return 'Daily'
                              case 'Weekly':
                                return 'Weekly'
                              case 'Monthly':
                                return 'Monthly'
                              case 'Quarterly':
                                return 'Quarterly'
                              case 'Yearly':
                                return 'Yearly'
                              case 'LocationCheckInStreak':
                                return 'Location Check-In Streak'
                              default:
                                return 'No'
                            }
                          })()}
                        </Typography>
                      </Grid>
                    )}
                    {badgeRewardTypeHasAccessToControl(
                      badgeRewardType,
                      'DependentOnBadge'
                    ) && (
                      <Grid item md={3}>
                        <Typography
                          variant="body2"
                          pb={1}
                          color="text.secondary"
                        >
                          Depends On Badge Id
                        </Typography>
                        <Typography variant="body-1">
                          {badgeReward?.dependentBadgeRewardId === 0
                            ? ''
                            : badgeReward?.dependentBadgeRewardId}
                        </Typography>
                      </Grid>
                    )}
                    {badgeRewardTypeHasAccessToControl(
                      badgeRewardType,
                      'LimitCheckins'
                    ) && (
                      <Grid item md={3}>
                        <Typography
                          variant="body2"
                          pb={1}
                          color="text.secondary"
                        >
                          Limit to 1 Check-in Per Day
                        </Typography>
                        <Typography variant="body-1">
                          {badgeReward?.limitCheckins === false ? 'No' : 'Yes'}
                        </Typography>
                      </Grid>
                    )}
                    {badgeRewardTypeHasAccessToControl(
                      badgeReward?.type,
                      'RewardsProgramId'
                    ) && (
                      <Grid item md={3}>
                        <Typography
                          variant="body2"
                          mb={1}
                          color="text.secondary"
                        >
                          Rewards Program
                        </Typography>
                        <Typography variant="body-1" mb={2}>
                          {rewardProgramQuery.data?.name ?? ''}
                        </Typography>
                      </Grid>
                    )}
                  </div>
                </Grid>
              </Grid>

              <Grid className="flex-1">
                <div className="pb-1 grid md:grid-cols-4 gap-y-6">
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'LocationPointsToAward'
                  ) && (
                    <Grid item md={3}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Location points to award
                      </Typography>
                      <Typography variant="body-1">
                        {badgeReward?.locationPointsAwarded === 0
                          ? ''
                          : badgeReward?.locationPointsAwarded}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'LocationSweepstakeEntriesToAward'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Location sweepstake entries to award
                      </Typography>
                      <Typography variant="body-1">
                        {badgeReward?.locationSweepstakeEntriesAwarded === 0
                          ? ''
                          : badgeReward?.locationSweepstakeEntriesAwarded}
                      </Typography>
                    </Grid>
                  )}
                  {badgeRewardTypeHasAccessToControl(
                    badgeRewardType,
                    'LocationSweepstakeEntriesToAward'
                  ) && (
                    <Grid item md={2}>
                      <Typography variant="body2" pb={1} color="text.secondary">
                        Location sweepstake
                      </Typography>
                      <Typography variant="body-1">
                        {locationSweepstakeName || ''}
                      </Typography>
                    </Grid>
                  )}
                </div>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Badge reports section */}
        <Box className="pt-4">
          <Box>
            <SectionHeader title="Badge Reports" showButton={false} />
            <Box
              border={{ md: `1px solid ${theme.palette.grey[200]}` }}
              borderRadius={'5px'}
              p={{ xs: 0, sm: 3 }}
            >
              <Grid
                container
                className="flex flex-row flex-wrap"
                columnGap={15}
              >
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Total Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {totalCount}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Unique Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {uniqueCount}
                  </Typography>
                </Grid>
                <Grid item md={2}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Total Location Points Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {totalLocationPointsCount}
                  </Typography>
                </Grid>
                <Grid item md={3}>
                  <Typography variant="body2" pb={1} color="text.secondary">
                    Total Location Sweepstake Entries Count
                  </Typography>
                  <Typography variant="body1" pb={2}>
                    {totalLocationSweepstakeEntriesCount}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>

        {/* Image section */}
        <Box>
          <SectionHeader
            title="Not Earned Image"
            onClickButton={async () =>
              await toggleImageModalIsOpen('notEarned')
            }
            buttonText="Add Image"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '16px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              px: '16px',
              py: '16px',
              mt: '12px',
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                sx={{ pt: { xs: '24px', sm: 0 } }}
              >
                <Typography variant="body-3" className="w-full">
                  Thumbnail Image
                </Typography>
                <SimpleImageCard
                  imageSource={badgeReward?.imageNotEarned}
                  sx={{ maxWidth: '358px', maxHeight: '358px' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* Image section */}
        <Box>
          <SectionHeader
            title="Earned Image"
            onClickButton={async () => await toggleImageModalIsOpen('earned')}
            buttonText="Add Image"
          />

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              rowGap: '16px',
              borderWidth: 1,
              borderColor: 'grey.300',
              borderRadius: 2,
              px: '16px',
              py: '16px',
              mt: '12px',
            }}
          >
            <Grid container>
              <Grid
                item
                xs={12}
                sm={12}
                md={12}
                sx={{ pt: { xs: '24px', sm: 0 } }}
              >
                <Typography variant="body-3" className="w-full">
                  Thumbnail Image
                </Typography>
                <SimpleImageCard
                  imageSource={badgeReward?.imageEarned}
                  sx={{ maxWidth: '358px', maxHeight: '358px' }}
                />
              </Grid>
            </Grid>
          </Box>
        </Box>

        <LocationBadgesEdit
          isModalOpen={isDetailsModalOpen}
          toggleModalIsOpen={toggleDetailsModalIsOpen}
          badgeReward={badgeReward}
          badgeRewards={locationBadges}
        />

        <BadgesUploadImage
          isModalOpen={isImageModalOpen}
          toggleModalIsOpen={toggleImageModalIsOpen}
          badgeReward={badgeReward}
          imageType={imageType}
        />

        <ViewLocationsModal
          isModalOpen={isViewLocationsModalOpen}
          toggleModalIsOpen={toggleViewLocationsModalIsOpen}
          licensedEstablishmentIds={badgeReward.licensedEstablishmentIds ?? []}
        />
      </Box>
    </Page>
  )
}
